import {
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
} from '@mui/material'
import { observer } from 'mobx-react-lite'
import type { FC } from 'react'
import { NavLink, useParams } from 'react-router-dom'
import { useAgentPrompts } from '../../../../stores/agent-prompts-store'
import type { Stores } from '../../../../util/store'

export interface OrganizationViPromptPageProps {
    stores: Stores
}

const NONE = '__none__'

export const OrganizationViPromptPage: FC<OrganizationViPromptPageProps> =
    observer(({ stores }) => {
        const { organizationId } = useParams()
        const prompts = useAgentPrompts(stores)

        const assigned = organizationId
            ? stores.agentPrompts.promptForOrganization(organizationId)
            : undefined
        const promoted = prompts.find(prompt => prompt.is_default)
        const effective = assigned ?? promoted

        const onChange = async (value: string) => {
            if (!organizationId) return
            await stores.agentPrompts.setOrganizationPrompt(
                organizationId,
                value === NONE ? undefined : value,
            )
        }

        return (
            <Paper
                data-testid="org-vi-prompt"
                sx={{ margin: '1rem', padding: '1rem', maxWidth: 640 }}>
                <h3 style={{ marginBottom: '0.75rem' }}>Vi 3.0 prompt</h3>

                {!stores.agentPrompts.isFetched ? (
                    <p className="color-dimmed">Loading…</p>
                ) : (
                    <>
                        <FormControl fullWidth size="small">
                            <InputLabel id="vi-prompt-label">
                                Prompt for this organization
                            </InputLabel>
                            <Select
                                labelId="vi-prompt-label"
                                label="Prompt for this organization"
                                data-testid="org-vi-prompt-select"
                                value={assigned?.id ?? NONE}
                                disabled={stores.agentPrompts.isUpdating}
                                onChange={e => onChange(e.target.value)}>
                                <MenuItem value={NONE}>
                                    {promoted
                                        ? `Use the promoted default — ${promoted.name}`
                                        : 'Use the built-in baseline'}
                                </MenuItem>
                                {prompts.map(prompt => (
                                    <MenuItem key={prompt.id} value={prompt.id}>
                                        {prompt.name}
                                        {prompt.is_default ? ' (default)' : ''}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <p
                            className="color-dimmed"
                            style={{ fontSize: '0.8rem', marginTop: '0.75rem' }}>
                            {assigned
                                ? 'This organization has its own prompt, so promoting a different default will not change what it sees.'
                                : 'This organization follows whatever is promoted to default. Pick a prompt to give it one of its own.'}
                        </p>

                        {/* Anchors, not buttons, so middle-click and
                            open-in-new-tab work; .text-button is the look. */}
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '1.5rem',
                                marginTop: '1rem',
                            }}>
                            {effective && (
                                <NavLink
                                    className="text-button"
                                    data-testid="org-vi-prompt-open"
                                    to={`/prompts/${effective.id}`}>
                                    {`Open “${effective.name}”`}
                                </NavLink>
                            )}

                            <NavLink
                                className="text-button text-button__comment"
                                data-testid="org-vi-prompt-all"
                                to="/prompts">
                                All prompts
                            </NavLink>
                        </div>
                    </>
                )}
            </Paper>
        )
    })
